import React from "react";
import { X } from "lucide-react";

import { ButtonShadcn } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

/**
 * Blanks a form field, without removing the thing the field belongs to.
 *
 * The rule it carries, in one place: the control is ALWAYS rendered and grows
 * disabled when there is nothing left to clear. A clear that appears only once
 * the field is filled is invisible exactly when someone is hunting for it, and
 * it makes the row change shape as they type. Pass `canClear` and let the
 * button decide — callers should not be hiding it conditionally.
 *
 * `ButtonShadcn variant="ghost" size="xs"` already carries the focus ring and
 * `disabled:pointer-events-none disabled:opacity-50`, which is the greyed-out
 * state this needs; nothing here re-states them.
 *
 * `shared/SearchInput.jsx` and `shared/DateRangeFilter.jsx` each still hand-roll
 * this same ✕ with their own class string. They are the next callers.
 *
 * @param {string} label      - Accessible name. Say which field it clears —
 *      "Clear language for row 2" reads on its own; "Clear" does not.
 * @param {boolean} canClear  - Does the field hold anything? Drives `disabled`.
 * @param {() => void} onClick
 * @param {boolean} [disabled] - The surrounding form is busy, or the field is
 *      not the user's to change. Independent of `canClear`; either disables.
 * @param {ReactNode} [children="Clear"] - Visible text. Pass `null` for an
 *      icon-only control, where `label` becomes the only accessible name.
 */
const ClearButton = ({ label, canClear, onClick, disabled = false, children = "Clear", className }) => (
  <ButtonShadcn
    type="button"
    variant="ghost"
    size="xs"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={disabled || !canClear}
    className={cn("shrink-0 text-text-secondary hover:text-red-400", className)}
  >
    <X aria-hidden="true" />
    {children}
  </ButtonShadcn>
);

export default ClearButton;
